import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  buildInvoicePdfHtmlDocument,
  computeInvoiceDocumentAmounts,
  waitForImagesInElement,
} from '../utils/invoicePdfHtml';
import { stampDevelloFooterOnAllPages } from '../utils/invoicePdfStamp';
import { calculateQuoteTotals } from '../utils/quoteTotals';
import {
  fetchWithAuth,
  DEVELLO_API_ORIGIN,
  withTimeout,
  safeRemoveDetachedNode,
  toUserMessage,
} from '../utils/asyncReliability';
import { supabase } from '../supabase';
import { devError } from '../utils/logger';

function buildInvoiceEmailCopy(client, contractorName, totalFormatted) {
  const name = client?.name?.trim() || 'there';
  const co = contractorName?.trim() || 'Our team';
  return {
    message: `Hi ${name},

Thank you for working with ${co}. Your invoice is attached as a PDF so you can open, save, or print it anytime.

Amount due: $${totalFormatted}

If anything on the invoice needs clarification or you'd like to discuss payment options, just reply to this email — we're happy to help.

We appreciate your business.

Warm regards,
${co}`,
  };
}

const EmailModal = ({
  isOpen,
  onClose,
  quoteData,
  userEmail,
  clients = [],
  settings = {},
  onQuoteUpdate,
  userPlan = 'free',
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [isLoadingPdf, setIsLoadingPdf] = useState(false);
  const [pdfObjectUrl, setPdfObjectUrl] = useState(null);
  const [showPdfLightbox, setShowPdfLightbox] = useState(false);

  const [formData, setFormData] = useState({
    toEmail: '',
    subject: '',
    message: '',
    companyName: '',
    contractorName: '',
    quoteNumber: '',
    totalAmount: 0,
    address: '',
  });

  const hasInvoicePDF = quoteData?.invoiceData;

  const calculateTotal = useCallback((qd) => {
    if (!qd) return 0;
    return calculateQuoteTotals(qd).total;
  }, []);

  useEffect(() => {
    if (!pdfBlob) {
      setPdfObjectUrl(null);
      return;
    }
    const url = URL.createObjectURL(pdfBlob);
    setPdfObjectUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [pdfBlob]);

  useEffect(() => {
    if (!quoteData) return;
    const client = quoteData.clientId ? clients.find((c) => c.id === quoteData.clientId) : null;
    const total = quoteData.invoiceData
      ? computeInvoiceDocumentAmounts(quoteData.invoiceData).total
      : calculateTotal(quoteData);
    const totalFormatted = total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const contractorName = settings.companyName || 'Your company';
    const { message } = buildInvoiceEmailCopy(client, contractorName, totalFormatted);

    setFormData((prev) => ({
      ...prev,
      companyName: client?.name || '',
      toEmail: client?.email || '',
      totalAmount: totalFormatted,
      address: client?.address || '',
      subject: '',
      message,
    }));
  }, [quoteData, clients, settings.companyName, calculateTotal]);

  useEffect(() => {
    if (!isOpen) {
      setCurrentStep(1);
      setPdfBlob(null);
      setIsLoadingPdf(false);
      setPdfObjectUrl(null);
      setFormData({
        toEmail: '',
        subject: '',
        message: '',
        companyName: '',
        contractorName: '',
        quoteNumber: '',
        totalAmount: 0,
        address: '',
      });
    }
  }, [isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const getPDFBlob = async () => {
    if (!quoteData?.invoiceData) return null;
    let pdfContent = null;
    try {
      const jsPDF = (await import('jspdf')).default;
      const html2canvas = (await import('html2canvas')).default;

      const invoiceData = quoteData.invoiceData;
      pdfContent = document.createElement('div');
      pdfContent.style.width = '760px';
      pdfContent.style.padding = '0';
      pdfContent.style.margin = '0';
      pdfContent.style.backgroundColor = '#ffffff';
      pdfContent.style.position = 'absolute';
      pdfContent.style.left = '-9999px';
      pdfContent.style.top = '0';

      pdfContent.innerHTML = await buildInvoicePdfHtmlDocument(invoiceData);
      document.body.appendChild(pdfContent);
      await waitForImagesInElement(pdfContent);
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

      const canvas = await withTimeout(
        html2canvas(pdfContent, {
          scale: 2.5,
          useCORS: true,
          allowTaint: false,
          backgroundColor: '#ffffff',
          logging: false,
        }),
        180000,
        'emailModal:invoiceHtml2canvas'
      );

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      if (userPlan !== 'pro') {
        await stampDevelloFooterOnAllPages(pdf);
      }
      return pdf.output('blob');
    } catch (error) {
      devError('Error generating invoice PDF:', error);
      alert(toUserMessage(error, 'Could not generate the invoice PDF. Please try again.'));
      return null;
    } finally {
      safeRemoveDetachedNode(pdfContent);
    }
  };

  const handleLoadPreview = async () => {
    if (!hasInvoicePDF) {
      alert('Create an invoice first: open Actions → Invoice on this job, then save.');
      return;
    }
    setIsLoadingPdf(true);
    try {
      const blob = await getPDFBlob();
      if (blob) {
        setPdfBlob(blob);
        setCurrentStep(2);
      }
    } finally {
      setIsLoadingPdf(false);
    }
  };

  const handleBack = () => {
    if (currentStep === 2) {
      setShowPdfLightbox(false);
      setCurrentStep(1);
    }
  };

  const handleDownloadPdf = useCallback(() => {
    if (!pdfBlob) return;
    const url = URL.createObjectURL(pdfBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Invoice_${quoteData?.quoteNumber || quoteData?.id?.slice(-6) || 'document'}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [pdfBlob, quoteData?.quoteNumber, quoteData?.id]);

  useEffect(() => {
    if (!showPdfLightbox) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        setShowPdfLightbox(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showPdfLightbox]);

  useEffect(() => {
    if (!showPdfLightbox) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.body.classList.add('devello-pdf-lightbox-active');
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.classList.remove('devello-pdf-lightbox-active');
    };
  }, [showPdfLightbox]);

  const [showSuccess, setShowSuccess] = useState(false);

  const sendEmailNow = async () => {
    if (!pdfBlob) {
      alert('No PDF ready. Go back and load the preview first.');
      return;
    }

    setIsSendingEmail(true);
    try {
      const arrayBuffer = await pdfBlob.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);
      const chunkSize = 8192;
      let binary = '';
      for (let i = 0; i < uint8Array.length; i += chunkSize) {
        const chunk = uint8Array.slice(i, i + chunkSize);
        binary += String.fromCharCode.apply(null, chunk);
      }
      const base64 = btoa(binary);

      const invNo =
        quoteData?.invoiceData?.invoiceNumber ||
        quoteData?.quoteNumber ||
        quoteData?.id?.slice(-8) ||
        'Invoice';

      const totalForEmail = quoteData?.invoiceData
        ? computeInvoiceDocumentAmounts(quoteData.invoiceData).total
        : calculateTotal(quoteData);
      const totalAmountFormatted = totalForEmail.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

      const emailData = {
        fromEmail: userEmail,
        toEmail: formData.toEmail,
        subject: formData.subject,
        message: formData.message,
        companyName: formData.companyName,
        contractorName: settings.companyName || 'Your company',
        quoteNumber: invNo,
        totalAmount: totalAmountFormatted,
        includePDF: true,
        pdfBase64: base64,
        companyInfo: {
          phone: settings.companyPhone || '',
          streetAddress: settings.streetAddress || '',
          city: settings.city || '',
          province: settings.province || '',
          postalCode: settings.postalCode || '',
          email: settings.companyEmail || userEmail || '',
        },
        isInvoiceDocument: true,
      };

      const sendRes = await fetchWithAuth(`${DEVELLO_API_ORIGIN}/api/send-quote-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(emailData),
        name: 'sendQuoteEmail',
        timeoutMs: 90000,
        retries: 1,
        supabaseClient: supabase,
        requireAuth: false,
        retryAuth: true,
      });

      if (sendRes.ok) {
        setShowPdfLightbox(false);
        setShowSuccess(true);
        if (onQuoteUpdate && quoteData) {
          const updatedQuote = { ...quoteData };
          updatedQuote.invoiceSent = true;
          updatedQuote.invoiceSentAt = new Date().toISOString();
          if (updatedQuote.quoteSent || updatedQuote.status === 'sent_quote' || updatedQuote.status === 'sent_both') {
            updatedQuote.status = 'sent_both';
          } else {
            updatedQuote.status = 'sent_invoice';
          }
          onQuoteUpdate(updatedQuote);
        }
      } else {
        alert(sendRes.userMessage);
      }
    } catch (error) {
      alert(toUserMessage(error, 'Could not send email. Please try again.'));
    } finally {
      setIsSendingEmail(false);
    }
  };

  if (!isOpen) return null;

  const docTitle =
    quoteData?.invoiceData?.documentTitle ||
    quoteData?.invoiceData?.invoiceNumber ||
    quoteData?.quoteNumber ||
    'Invoice';

  const pdfLightboxOpen = showPdfLightbox && !!pdfObjectUrl;

  const pdfIframeSrc =
    pdfObjectUrl != null
      ? `${pdfObjectUrl}#toolbar=0&navpanes=0`
      : '';

  return (
    <>
    {!pdfLightboxOpen && (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/55 backdrop-blur-sm p-3 sm:p-4">
      <div className="flex h-[min(96vh,1040px)] w-full max-w-[min(96vw,1280px)] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200/80">
        {/* Header */}
        <div className="flex shrink-0 items-start justify-between gap-4 border-b border-slate-100 bg-gradient-to-r from-teal-600 via-teal-700 to-sky-800 px-5 py-4 sm:px-6">
          <div>
            <h2 className="text-xl font-bold tracking-tight text-white sm:text-2xl">Send invoice</h2>
            <p className="mt-0.5 text-sm text-teal-100">Preview your PDF, then send a polished email to your client</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-teal-100 transition hover:bg-white/10 hover:text-white"
            aria-label="Close"
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Step indicator */}
        <div className="shrink-0 border-b border-slate-100 bg-slate-50 px-5 py-3 sm:px-6">
          <div className="mx-auto flex max-w-lg items-center justify-center gap-3">
            <div
              className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold ${
                currentStep >= 1 ? 'bg-teal-600 text-white' : 'bg-slate-200 text-slate-500'
              }`}
            >
              1
            </div>
            <div className={`h-0.5 flex-1 max-w-[120px] rounded ${currentStep >= 2 ? 'bg-teal-600' : 'bg-slate-200'}`} />
            <div
              className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold ${
                currentStep >= 2 ? 'bg-teal-600 text-white' : 'bg-slate-200 text-slate-500'
              }`}
            >
              2
            </div>
            <span className="ml-2 hidden text-sm font-medium text-slate-600 sm:inline">
              {currentStep === 1 ? 'Prepare' : 'Preview & send'}
            </span>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="min-h-0 flex-1 overflow-hidden bg-slate-50/80">
          {currentStep === 1 && (
            <div className="flex h-full flex-col items-center justify-center overflow-y-auto p-6 sm:p-10">
              <div className="w-full max-w-md text-center">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-teal-100 text-teal-700">
                  <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-slate-900">Load your invoice PDF</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">
                  We’ll generate the same PDF your client will receive. Next, you’ll compose the email and can open a full-screen preview anytime before sending.
                </p>
                <div
                  className={`mt-8 rounded-2xl border-2 p-6 text-left transition ${
                    hasInvoicePDF
                      ? 'border-teal-200 bg-white shadow-sm'
                      : 'border-slate-200 bg-slate-100/80 opacity-80'
                  }`}
                >
                  <p className="text-sm font-semibold text-slate-800">{docTitle}</p>
                  <p className="mt-1 text-xs text-slate-500">
                    {hasInvoicePDF
                      ? 'Invoice data is ready. Continue below to build the preview.'
                      : 'No invoice yet — use Actions → Invoice on this job first.'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div className="flex h-full min-h-0 flex-col overflow-hidden bg-white">
              <div className="shrink-0 border-b border-slate-100 px-5 py-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Email</p>
                    <p className="text-sm font-bold text-slate-900">What your client receives</p>
                    <p className="mt-1 text-xs text-slate-500">
                      Add a subject line if you like — it’s blank by default. The message and branded layout are sent with the PDF.
                    </p>
                  </div>
                  {pdfObjectUrl && (
                    <div className="flex shrink-0 flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setShowPdfLightbox(true)}
                        className="inline-flex items-center justify-center gap-2 rounded-xl border border-teal-200 bg-teal-50 px-4 py-2.5 text-sm font-bold text-teal-800 shadow-sm transition hover:bg-teal-100"
                      >
                        <svg className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                          />
                        </svg>
                        Preview PDF
                      </button>
                      <button
                        type="button"
                        onClick={handleDownloadPdf}
                        className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
                      >
                        Download
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto">
                <div className="space-y-4 p-5">
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold text-slate-700">From</label>
                    <input
                      type="email"
                      value="notifications@devellotech.ca"
                      disabled
                      className="w-full cursor-not-allowed rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-500"
                    />
                    <p className="mt-1 text-[11px] text-slate-400">Replies go to your account email</p>
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold text-slate-700">To *</label>
                    <input
                      type="email"
                      name="toEmail"
                      value={formData.toEmail}
                      onChange={handleInputChange}
                      required
                      className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20"
                      placeholder="client@example.com"
                    />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold text-slate-700">Subject (optional)</label>
                    <input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20"
                      placeholder="Leave blank to use a default subject line"
                    />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold text-slate-700">Message *</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={12}
                      className="w-full resize-y rounded-xl border border-slate-200 px-3 py-2.5 text-sm leading-relaxed focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20 min-h-[200px]"
                      placeholder="Your message…"
                    />
                  </div>
                  <div className="rounded-xl border border-teal-100 bg-teal-50/80 px-4 py-3">
                    <p className="text-xs font-semibold text-teal-900">Attachment</p>
                    <p className="mt-0.5 text-xs text-teal-800/90">
                      Invoice PDF is included automatically. Use <span className="font-semibold">Preview PDF</span> above to
                      view it full screen before sending.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Fixed bottom bar */}
        <div className="shrink-0 border-t border-slate-200 bg-white px-4 py-4 shadow-[0_-4px_20px_rgba(15,23,42,0.06)] sm:px-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <button
              type="button"
              onClick={currentStep === 2 ? handleBack : onClose}
              disabled={isSendingEmail || isLoadingPdf}
              className="inline-flex min-h-[44px] min-w-[100px] items-center justify-center rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {currentStep === 2 ? (
                <>
                  <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Back
                </>
              ) : (
                'Cancel'
              )}
            </button>

            {currentStep === 1 ? (
              <button
                type="button"
                onClick={handleLoadPreview}
                disabled={!hasInvoicePDF || isLoadingPdf}
                className="inline-flex min-h-[44px] min-w-[160px] items-center justify-center rounded-xl bg-gradient-to-r from-teal-600 to-sky-700 px-6 py-2.5 text-sm font-bold text-white shadow-md transition hover:from-teal-700 hover:to-sky-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isLoadingPdf ? (
                  <>
                    <svg className="mr-2 h-5 w-5 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Building preview…
                  </>
                ) : (
                  <>
                    Next
                    <svg className="ml-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </>
                )}
              </button>
            ) : (
              <button
                type="button"
                onClick={sendEmailNow}
                disabled={isSendingEmail || !pdfBlob}
                className="inline-flex min-h-[44px] min-w-[160px] items-center justify-center rounded-xl bg-gradient-to-r from-teal-600 to-sky-700 px-6 py-2.5 text-sm font-bold text-white shadow-md transition hover:from-teal-700 hover:to-sky-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isSendingEmail ? (
                  <>
                    <svg className="mr-2 h-5 w-5 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Sending…
                  </>
                ) : (
                  <>
                    <svg className="mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                    Send email
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
    )}

      {typeof document !== 'undefined' &&
        pdfLightboxOpen &&
        createPortal(
          <div
            className="devello-invoice-pdf-lightbox flex min-h-0 flex-col bg-gradient-to-b from-slate-50 via-slate-100 to-slate-100"
            style={{
              position: 'fixed',
              inset: 0,
              width: '100%',
              minHeight: '100%',
              height: '100%',
              zIndex: 2147483647,
            }}
            role="dialog"
            aria-modal="true"
            aria-label="Invoice PDF preview"
          >
            <div className="flex shrink-0 items-start justify-between gap-4 border-b border-teal-500/30 bg-gradient-to-r from-teal-600 via-teal-700 to-sky-800 px-5 py-4 sm:px-6">
              <div className="min-w-0">
                <h2 className="text-xl font-bold tracking-tight text-white sm:text-2xl">Invoice PDF — full preview</h2>
                <p className="mt-0.5 text-sm text-teal-100">Review the attachment before you send</p>
              </div>
              <button
                type="button"
                onClick={() => setShowPdfLightbox(false)}
                className="shrink-0 rounded-lg px-4 py-2 text-sm font-semibold text-white ring-1 ring-white/40 transition hover:bg-white/10"
              >
                Close
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-hidden bg-slate-100/90 p-3 sm:p-4 md:p-6">
              <div className="mx-auto h-full max-w-5xl overflow-hidden rounded-xl bg-white shadow-lg ring-1 ring-slate-200/80">
                <iframe
                  title="Invoice PDF full preview"
                  src={pdfIframeSrc}
                  className="block h-full min-h-0 w-full border-0 bg-white"
                />
              </div>
            </div>
          </div>,
          document.body
        )}

      {!pdfLightboxOpen && showSuccess && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-2xl ring-1 ring-slate-200">
            <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
              <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-center text-xl font-bold text-slate-900">Email sent</h3>
            <p className="mt-2 text-center text-sm text-slate-600">
              Your invoice went to <span className="font-semibold text-teal-700">{formData.toEmail}</span> with the PDF
              attached.
            </p>
            <button
              type="button"
              onClick={() => {
                setShowSuccess(false);
                onClose();
              }}
              className="mt-8 w-full rounded-xl bg-gradient-to-r from-teal-600 to-sky-700 py-3 text-sm font-bold text-white shadow-md hover:from-teal-700 hover:to-sky-800"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default EmailModal;
